import React, { useCallback, useState } from "react";
import { Button, Descriptions, Modal, message } from "antd";
import dayjs from "dayjs";

import { EvidenceRef } from "../../types/healthcare/PatientAnalysisTypes";
import { Patient } from "../../types/healthcare/PatientTypes";
import { MedicalHistory, historyTypesOptions } from "../../types/healthcare/MedicalHistoryTypes";
import { Medication, defaultMedication } from "../../types/healthcare/MedicationTypes";

import { fetchMedicalHistoryDetail } from "../../api/healthcare/PatientApi";
import { fetchSingleMedicationById } from "../../api/healthcare/MedicationsApi";

import { useOpenTestOrderModal } from "./useOpenTestOrderModal";
import CreateMedicalHistoryModal from "../../pages/healthcare/patient/modalComponents/submodals/CreateMedicalHistoryModal";
import CreateMedicationModal from "../../pages/healthcare/patient/modalComponents/submodals/CreateMedicationModal";
import type { UserUuid } from "../../types/identity";

/**
 * Opens the detail modal for the original record behind an evidence reference, so a
 * practitioner can click a reference (shown on a piece of AI-generated info) and jump
 * straight to that record's full data.
 *
 * Local-state pattern (no global store): the hook owns the open modal + its fetched
 * record and exposes `openRef(ref)` to route by `ref.source`, plus `modals` to render
 * once near the surface that triggers the clicks. Every resolution path degrades
 * gracefully — a missing `record_id`, a not-found record, or a failed fetch warns and
 * no-ops rather than throwing.
 */
export function useReferenceModal(
	patientId: UserUuid,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	patient?: Patient,
): { openRef: (ref: EvidenceRef) => void; modals: React.ReactNode } {
	// Test order (blood/lab) path is delegated to the shared, reusable hook. It owns the
	// modal fetch/state and renders its own `modals` (merged into ours below).
	const { openTestOrder: openTestOrderModal, modals: testOrderModals } = useOpenTestOrderModal();

	// Medical history modal state
	const [medicalHistory, setMedicalHistory] = useState<Partial<MedicalHistory> | null>(null);
	const [medicalHistoryOpen, setMedicalHistoryOpen] = useState(false);

	// Medication modal state
	const [medication, setMedication] = useState<Medication | null>(null);
	const [medicationOpen, setMedicationOpen] = useState(false);

	const openTestOrder = useCallback((ref: EvidenceRef) => {
		const recordId = Number(ref.record_id);
		if (!ref.record_id || Number.isNaN(recordId)) {
			message.warning("This reference has no linked test order.");
			return;
		}
		openTestOrderModal(patientId, recordId);
	}, [patientId, openTestOrderModal]);

	const openMedicalHistory = useCallback(async (ref: EvidenceRef) => {
		const recordId = Number(ref.record_id);
		if (!ref.record_id || Number.isNaN(recordId)) {
			message.warning("This reference has no linked medical history record.");
			return;
		}
		const hide = message.loading("Loading medical history…", 0);
		try {
			const record = await fetchMedicalHistoryDetail(recordId);
			if (!record) {
				message.warning("The referenced medical history record could not be found.");
				return;
			}
			setMedicalHistory(record);
			setMedicalHistoryOpen(true);
		} catch {
			message.error("Failed to load the medical history record.");
		} finally {
			hide();
		}
	}, []);

	const openMedication = useCallback(async (ref: EvidenceRef) => {
		const recordId = Number(ref.record_id);
		if (!ref.record_id || Number.isNaN(recordId)) {
			message.warning("This reference has no linked medication record.");
			return;
		}
		const hide = message.loading("Loading medication…", 0);
		try {
			const { data } = await fetchSingleMedicationById(recordId);
			if (!data) {
				message.warning("The referenced medication could not be found.");
				return;
			}
			setMedication(data);
			setMedicationOpen(true);
		} catch {
			message.error("Failed to load the medication.");
		} finally {
			hide();
		}
	}, []);

	const openRef = useCallback((ref: EvidenceRef) => {
		switch (ref.source) {
			case "blood_results":
			case "lab_results":
				void openTestOrder(ref);
				break;
			case "medical_history":
				void openMedicalHistory(ref);
				break;
			case "medications":
				void openMedication(ref);
				break;
			case "genetic_panel":
				// No clean standalone detail modal keyed by sample/genetic test exists in the
				// clinical app — the genetic viewer is embedded in the lab pages. Best-effort
				// no-op rather than building new genetics UI.
				message.info("Genetic panel details are available in the patient's genetic report.");
				break;
			default:
				// Soft sources (diet/exercise/questionnaire) are not interactive.
				break;
		}
	}, [openTestOrder, openMedicalHistory, openMedication]);

	const historyTypeLabel = medicalHistory?.history_type
		? historyTypesOptions.find((o) => o.value === medicalHistory.history_type)?.label ?? medicalHistory.history_type
		: null;

	const fmtDate = (d: Date | string | null | undefined): string => {
		if (!d) return "—";
		const parsed = dayjs(d as string);
		return parsed.isValid() ? parsed.format("D MMM YYYY") : String(d);
	};

	const modals = (
		<>
			{testOrderModals}

			{medicalHistoryOpen && medicalHistory && (
				<Modal
					title="Medical History"
					open={medicalHistoryOpen}
					onCancel={() => setMedicalHistoryOpen(false)}
					width="85vw"
					footer={[
						<Button key="close" onClick={() => setMedicalHistoryOpen(false)}>
							Close
						</Button>,
					]}
					destroyOnClose
				>
					<Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
						<Descriptions.Item label="Type">{historyTypeLabel ?? "—"}</Descriptions.Item>
						<Descriptions.Item label="Severity">{medicalHistory.severity ?? "—"}</Descriptions.Item>
						<Descriptions.Item label="Description" span={2}>
							{medicalHistory.description || "—"}
						</Descriptions.Item>
						<Descriptions.Item label="Date Identified">{fmtDate(medicalHistory.date_identified)}</Descriptions.Item>
						<Descriptions.Item label="Date Resolved">{fmtDate(medicalHistory.date_resolved)}</Descriptions.Item>
					</Descriptions>
					<CreateMedicalHistoryModal
						isEditing={true}
						currentRecord={medicalHistory}
						setCurrentRecord={setMedicalHistory}
					/>
				</Modal>
			)}

			{medicationOpen && medication && (
				<CreateMedicationModal
					currentMedication={medication ?? defaultMedication}
					setCurrentMedication={setMedication as React.Dispatch<React.SetStateAction<Medication>>}
					isEditing={false}
					isVisible={medicationOpen}
					onClose={() => setMedicationOpen(false)}
					isConsult={false}
				/>
			)}
		</>
	);

	return { openRef, modals };
}

export default useReferenceModal;
